import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { useAuth } from '@/contexts/AuthContext';
import { useBusiness } from '@/contexts/BusinessContext';
import { useBusinessDiscountForManagement } from '@/hooks/useBusinessDiscount';
import { CHAT_ROLE } from '@/utils/ChatUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, MessageCircle, AlertCircle, Lock, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import ChatSidebar from '@/components/ChatSidebar';
import UserConversationGroup from '@/components/chat/UserConversationGroup';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { groupConversationsByUser } from '@/utils/conversationGroupingUtils';
import { supabase } from '@/integrations/supabase/client';
import type { Conversation } from '@/types/chat';

const BusinessChat = () => {
  const { user, loading: authLoading } = useAuth();
  const { userBusinessId, isLoading: businessLoading } = useBusiness();
  const { data: businessDiscount } = useBusinessDiscountForManagement(userBusinessId);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { 
    conversations, 
    loadingConversations, 
    loadConversation,
    loadAllConversations,
  } = useChat();
  
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedCounterpart, setSelectedCounterpart] = useState<{
    id: string;
    name: string;
    logo: string | null;
    userRole: string;
  } | null>(null);
  
  const [directConversationError, setDirectConversationError] = useState<string | null>(null);
  const [loadingDirectConversation, setLoadingDirectConversation] = useState(false);
  const [hasDirectMessaging, setHasDirectMessaging] = useState<boolean | null>(null);
  const [checkingFeature, setCheckingFeature] = useState(true);
  
  // State for managing visible conversations per user (starts at 3, increases by 3)
  const [visibleConversationsPerUser, setVisibleConversationsPerUser] = useState<Record<string, number>>({});
  
  const conversationsLoadedRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  // Determine if we should show restricted view (paid business without subscription)
  const shouldShowRestricted = businessDiscount?.type !== 'Free' && hasDirectMessaging === false;
  
  // Redirect if user is not a business owner
  useEffect(() => {
    if (!businessLoading && !userBusinessId) {
      navigate('/admin');
    }
  }, [businessLoading, userBusinessId, navigate]);
  
  // Check if business has direct_messaging_enhanced feature
  useEffect(() => {
    const checkFeature = async () => {
      if (!userBusinessId) {
        setCheckingFeature(false);
        return;
      }

      setCheckingFeature(true);
      try {
        const { data, error } = await supabase.rpc('has_feature', {
          business_id: userBusinessId,
          feature_key: 'direct_messaging_enhanced'
        });

        if (error) {
          console.error('Error checking messaging feature:', error);
          setHasDirectMessaging(false);
        } else {
          setHasDirectMessaging(data);
        }
      } catch (error) {
        console.error('Error checking messaging feature:', error);
        setHasDirectMessaging(false);
      } finally {
        setCheckingFeature(false);
      }
    };

    checkFeature();
  }, [userBusinessId]);

  useEffect(() => {
    let isMounted = true;
    
    const loadConversationsOnce = async () => {
      if (user && isMounted && (!conversationsLoadedRef.current || userIdRef.current !== user.id) && !businessLoading) {
        try {
          console.log('[BusinessChat] Loading business conversations for user:', user.id, 'businessLoading:', businessLoading);
          await loadAllConversations();
          conversationsLoadedRef.current = true;
          userIdRef.current = user.id;
        } catch (error) {
          console.error('[BusinessChat] Error loading conversations:', error);
          toast({
            title: "Error loading conversations",
            description: "There was a problem loading your conversations. Please try again.",
            variant: "destructive"
          });
        }
      } else if (businessLoading) {
        console.log('[BusinessChat] Waiting for business context to load before fetching conversations');
      }
    };
    
    loadConversationsOnce();
    
    return () => {
      isMounted = false;
    };
  }, [user, businessLoading, toast, loadAllConversations]);

  const handleOpenChat = useCallback((conversation: Conversation) => {
    console.log(`[BusinessChat] Opening chat for conversation ID: ${conversation.id}`);
    const isUserBusiness = conversation.member_id !== user?.id;
    
    if (chatOpen) {
      console.log('[BusinessChat] Closing existing chat to reset state');
      setChatOpen(false);
      
      setTimeout(() => {
        setCounterpartAndOpenChat();
      }, 300);
    } else {
      setCounterpartAndOpenChat();
    }
    
    function setCounterpartAndOpenChat() {
      if (isUserBusiness && conversation.member) {
        // Use username instead of full name
        const memberName = conversation.member.username || conversation.member.email || 'User';
          
        console.log(`[BusinessChat] Setting counterpart to member: ${memberName}`);
        setSelectedCounterpart({
          id: conversation.member_id,
          name: memberName,
          logo: conversation.member.avatar_url || null,
          userRole: CHAT_ROLE.BUSINESS_OWNER
        });
      } else {
        console.log(`[BusinessChat] Setting counterpart to business: ${conversation.business?.name || 'Business'}`);
        setSelectedCounterpart({
          id: conversation.business_id,
          name: conversation.business?.name || 'Business',
          logo: conversation.business?.company_logo || null,
          userRole: CHAT_ROLE.COMMUNITY_MEMBER
        });
      }
      
      console.log('[BusinessChat] Opening chat sidebar');
      setChatOpen(true);
      
      setTimeout(() => {
        console.log(`[BusinessChat] Loading conversation ID: ${conversation.id}`);
        loadConversation(conversation.id);
      }, 100);
    }
  }, [user, chatOpen, loadConversation]);
  
  useEffect(() => {
    if (!user || !conversationId || loadingConversations || businessLoading) return;
    
    const openDirectConversation = async () => {
      console.log(`[BusinessChat] Attempting to open conversation directly from URL: ${conversationId}`);
      setLoadingDirectConversation(true);
      setDirectConversationError(null);
      
      try {
        const targetConversation = conversations.find(c => c.id === conversationId);
        
        if (targetConversation) {
          console.log(`[BusinessChat] Found conversation in list, opening: ${targetConversation.id}`);
          handleOpenChat(targetConversation);
          
          const element = document.getElementById(`conversation-${conversationId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('ring-2', 'ring-primary', 'ring-opacity-50');
            setTimeout(() => {
              element.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50');
            }, 2000);
          }
        } else {
          console.log(`[BusinessChat] Conversation not found in list: ${conversationId}`);
          
          if (conversations.length > 0 && !loadingConversations) {
            setDirectConversationError("The conversation you're looking for was not found.");
            toast({
              title: "Conversation not found",
              description: "The conversation you're trying to access does not exist or you don't have permission to view it.",
              variant: "destructive"
            });
          } else {
            console.log('[BusinessChat] Skipping error message as conversations may still be loading');
          }
        }
      } catch (error) {
        console.error('[BusinessChat] Error opening direct conversation:', error);
        setDirectConversationError("There was an error loading this conversation.");
      } finally {
        setLoadingDirectConversation(false);
      }
    };
    
    openDirectConversation();
  }, [conversationId, conversations, user, loadingConversations, businessLoading, toast, handleOpenChat]);

  // Filter and group conversations
  const userGroups = useMemo(() => {
    // Filter conversations to only show those where individuals contacted this business
    const businessConversations = conversations.filter(conv => conv.business_id === userBusinessId);
    
    // Group conversations by user
    return groupConversationsByUser(businessConversations);
  }, [conversations, userBusinessId]);

  // Handlers for load more / collapse functionality
  const handleLoadMore = useCallback((userId: string) => {
    setVisibleConversationsPerUser(prev => ({
      ...prev,
      [userId]: (prev[userId] || 3) + 3
    }));
  }, []);

  const handleCollapse = useCallback((userId: string) => {
    setVisibleConversationsPerUser(prev => ({
      ...prev,
      [userId]: 3
    }));
  }, []);

  const renderUserGroupList = () => {
    if (userGroups.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <MessageCircle className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-medium mb-2">No customer conversations yet</h3>
            <p className="text-muted-foreground max-w-md">
              When community members contact your business through chat, their conversations will appear here.
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-4 sm:space-y-6">
        {userGroups.map((group) => (
          <UserConversationGroup
            key={group.userId}
            group={group}
            onSelectConversation={handleOpenChat}
            visibleCount={visibleConversationsPerUser[group.userId] || 3}
            onLoadMore={handleLoadMore}
            onCollapse={handleCollapse}
            highlightedConversationId={conversationId}
          />
        ))}
      </div>
    );
  };

  const renderError = () => {
    if (!directConversationError) return null;
    
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{directConversationError}</AlertDescription>
      </Alert>
    );
  };

  const isLoading = loadingConversations || loadingDirectConversation || businessLoading;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="container mx-auto py-4 sm:py-6 md:py-8 px-4"
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Customer Conversations</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">
            View and respond to messages from community members who have contacted your business.
          </p>
        </div>
        
        
        {renderError()}
        
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">
              {businessLoading ? "Loading business information..." : "Loading conversations..."}
            </p>
          </div>
        ) : (
          <div className={`relative ${shouldShowRestricted ? 'min-h-[400px]' : ''}`}>
            {/* Blur effect for restricted content */}
            <div className={shouldShowRestricted ? 'blur-restricted' : ''}>
              {renderUserGroupList()}
            </div>
            
            {/* Overlay for restricted access */}
            {shouldShowRestricted && (
              <div className="blur-overlay">
                <div className="text-center space-y-3 w-full max-w-sm mx-auto">
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    <h3 className="text-lg sm:text-xl font-bold mb-2">
                      {userGroups.length > 0 ? `${userGroups.length} Customer${userGroups.length !== 1 ? 's' : ''} Trying to Reach You!` : 'Ready to Connect with Customers!'}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                    {userGroups.length > 0 
                      ? 'Activate your subscription to reply to customer messages and build lasting relationships.'
                      : 'Activate your subscription to reply to customer messages when they contact you.'
                    }
                  </p>
                  <Button
                    onClick={() => navigate('/admin/business/subscription')}
                    size="sm"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white gap-2 w-full sm:w-auto text-xs sm:text-sm"
                  >
                    <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                    Unlock Business
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {selectedCounterpart && (
        <ChatSidebar
          open={chatOpen}
          onOpenChange={setChatOpen}
          counterpartId={selectedCounterpart.id}
          counterpartName={selectedCounterpart.name}
          counterpartLogo={selectedCounterpart.logo}
          userRole={selectedCounterpart.userRole}
          disabled={hasDirectMessaging === false}
          disabledMessage={hasDirectMessaging === false ? "Upgrade your subscription to send messages" : undefined}
        />
      )}
    </motion.div>
  );
};

export default BusinessChat;